import { useDeepEqualMemo, useEventHandler } from '@superdispatch/hooks';
import { useEffect, useState } from 'react';
import { logWarning } from './Logger';

function isIOS() {
  const isIOSDevice = /iPhone|iPad|iPod/i.test(navigator.userAgent);
  const isIPadOS13Up =
    /Macintosh/i.test(navigator.userAgent) &&
    !!navigator.maxTouchPoints &&
    navigator.maxTouchPoints > 2;

  return isIOSDevice || isIPadOS13Up;
}

export function getPlatform(): 'mobile' | 'desktop' {
  const isMobile =
    isIOS() ||
    /Android|Windows Phone|BlackBerry|webOS/i.test(navigator.userAgent);

  return isMobile ? 'mobile' : 'desktop';
}

export function getOS(): 'ios' | 'android' | 'unknown' {
  return isIOS()
    ? 'ios'
    : /android/i.test(navigator.userAgent)
      ? 'android'
      : 'unknown';
}

/** @knipignore */
export function getBrowser():
  | 'chrome'
  | 'firefox'
  | 'safari'
  | 'edge'
  | 'opera'
  | 'unknown' {
  if (/opera|OPR\//i.test(navigator.userAgent)) {
    return 'opera';
  }

  if (/edg/i.test(navigator.userAgent)) {
    return 'edge';
  }

  if (/firefox/i.test(navigator.userAgent)) {
    return 'firefox';
  }

  if (/chrome/i.test(navigator.userAgent)) {
    return 'chrome';
  }

  if (/safari/i.test(navigator.userAgent)) {
    return 'safari';
  }

  return 'unknown';
}

export function useVisibilityState(): DocumentVisibilityState {
  const [state, setState] = useState<DocumentVisibilityState>(
    () => document.visibilityState,
  );

  useEffect(() => {
    function handleVisibilityChange() {
      setState(document.visibilityState);
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [setState]);

  return state;
}

export function useOnlineStatus() {
  const [status, setStatus] = useState<'online' | 'offline'>('online');

  useEffect(() => {
    const handleOnline = () => {
      setStatus('online');
    };
    const handleOffline = () => {
      setStatus('offline');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return status;
}

export function isChromium() {
  // For e2e tests
  if ('Cypress' in window) {
    return true;
  }

  return 'chrome' in window;
}

export function useMutationObserver(
  targetNode: HTMLElement | null,
  callback: (mutations: MutationRecord[]) => void,
  config: MutationObserverInit = {},
) {
  const configMemo = useDeepEqualMemo(() => config, [config]);
  const callbackHandler = useEventHandler(callback);

  useEffect(() => {
    if (!targetNode) {
      return;
    }

    const observer = new MutationObserver(callbackHandler);
    observer.observe(targetNode, configMemo);

    return () => {
      observer.disconnect();
    };
  }, [configMemo, targetNode, callbackHandler]);
}

export function loadScript({
  src,
  async = true,
}: {
  src: string;
  async?: boolean;
}): Promise<void> {
  return new Promise((resolve) => {
    const existingScript = document.querySelector(`script[src="${src}"]`);
    if (existingScript) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = src;
    script.async = async;
    script.onload = () => {
      resolve();
    };
    script.onerror = (error) => {
      logWarning('Failed to load script', { error });
    };
    document.head.appendChild(script);
  });
}
